import type Store from '@ember-data/store'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { action } from '@ember/object'
import { task } from 'ember-concurrency'
import { trackedFunction } from 'reactiveweb/function'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import {
  sharesClassQueryFilter,
  studentStatsClassQueryFilter,
} from 'teacher-ui-client/subscription-type/book-library/shares/index/route'
import { ByStudentTabs } from 'teacher-ui-client/subscription-type/book-library/shares/index/by-student/controller'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import {
  SchoolClassOptionTypes,
  AllClassesOption,
  MyClassesOption,
} from 'teacher-ui-client/components/base/select/school-class/options'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class'
import { BookLibraryCollectionStats } from 'teacher-ui-client/components/book-library/collection-stats'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { hash, array } from '@ember/helper'
import { t } from 'ember-intl'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { on } from '@ember/modifier'
import { or } from 'ember-truth-helpers'
import { pick } from 'teacher-ui-client/helpers/pick'
import type StudentStat from 'teacher-ui-client/models/book-library/student-stat'

interface Args {
  subscriptionType: SubscriptionType
}

interface Signature {
  Args: Args
  Blocks: {
    table: []
  }
}

export class PageBookLibraryShares extends Component<Signature> {
  @service store!: Store

  @service session!: SessionService

  @service router!: RouterService

  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service fileDownload!: FileDownload

  @queryParam('subscription-type/book-library/shares/index') search!: string

  @queryParam('subscription-type/book-library/shares/index') schoolClassId!: string | SchoolClassOptionTypes

  @queryParam('subscription-type/book-library/shares/index/by-collection') status!: ShareStatus[]

  @queryParam('subscription-type/book-library/shares/index/by-student') tab!: ByStudentTabs

  ShareStatus = ShareStatus

  ByStudentTabs = ByStudentTabs

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  get isByCollection() {
    return this.router.currentRouteName?.includes('by-collection')
  }

  get isByStudent() {
    return this.router.currentRouteName?.includes('by-student')
  }

  SchoolClassOptionTypes = SchoolClassOptionTypes

  get schoolClassAdditionalOptions() {
    return this.isAllStudentsTab ? [AllClassesOption, MyClassesOption] : [MyClassesOption]
  }

  get isCurrentSharesTab() {
    return this.isByCollection && !this.status.includes(ShareStatus.Expired)
  }

  get isPastSharesTab() {
    return this.isByCollection && this.status.includes(ShareStatus.Expired)
  }

  get isMyStudentsTab() {
    return this.isByStudent && this.tab === ByStudentTabs.MyStudents
  }

  get isAllStudentsTab() {
    return this.isByStudent && this.tab === ByStudentTabs.AllStudents
  }

  /**
   * Loads school classes and groups them for use in the school class selector
   */
  loadSchoolClasses = trackedFunction(this, async () => {
    const { subscriptionType } = this.args
    const { currentTeacher } = this.session

    await Promise.resolve() // disconnect tracking before store calls

    const schoolClasses = await this.store.query<SchoolClass>('school-class', {})
    return schoolClasses.reduce<{ allClasses: SchoolClass[]; myClasses: SchoolClass[] }>(
      (groupedClasses, sc) => {
        const showClass = sc.subscriptionType === subscriptionType && sc.hasStudents
        if (showClass) {
          const isMyClass = currentTeacher.schoolClasses.some((c) => c.id === sc.id)
          return {
            ...groupedClasses,
            allClasses: [...groupedClasses.allClasses, sc],
            myClasses: [...groupedClasses.myClasses, ...(isMyClass ? [sc] : [])],
          }
        } else {
          return groupedClasses
        }
      },
      { allClasses: [], myClasses: [] },
    )
  })

  /*
   * We want to reset the pagination when filtering, to avoid situations where the user is on page X but they then
   * apply filtering which reduces the page count below X, making their current page number not valid. It also improves
   * the user experience.
   *
   * Using a transition to set the search / schoolClassId allows us to also reset the page query param, which appears
   * on two child pages.
   */

  @action
  updateSchoolClassId(schoolClassId: string) {
    void this.router.transitionTo({ queryParams: { schoolClassId, page: 1 } })
  }

  @action
  changeSearch(search: string) {
    void this.router.transitionTo({ queryParams: { search, page: 1 } })
  }

  querySharesForExport = task({ restartable: true }, async () => {
    const exportQuery = {
      page: 'all',
      filter: {
        status: this.status.join(','),
        ...sharesClassQueryFilter(this.schoolClassId, this.store, this.session),
      },
    }

    return this.store.query<BookLibraryShare>('book-library/share', {
      ...exportQuery,
      // @ts-expect-error - include types not correct
      include: 'collection,collection.books,collection.books.book-metadata',
    })
  })

  queryStudentsForExport = task({ restartable: true }, async () => {
    const exportQuery = {
      page: 'all',
      filter: {
        school_id: this.session.currentSchool.id,
        ...studentStatsClassQueryFilter(this.schoolClassId, this.store, this.session),
      },
    }

    return this.store.query<StudentStat>('book-library/student-stat', { ...exportQuery })
  })

  @action
  async exportCsv() {
    if (this.isByStudent) {
      const studentStats = await this.queryStudentsForExport.perform()

      const header = [
        this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.headers.firstName'),
        this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.headers.lastName'),
        this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.headers.schoolClasses'),
        this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.headers.grade'),
        this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.headers.currentCollectionCount'),
      ]

      const rows = studentStats.map((studentStat) => {
        return [
          studentStat.firstName,
          studentStat.lastName,
          studentStat.schoolClassNames,
          this.gradeSets.nameFromGradeSetByPosition(studentStat.gradePosition),
          studentStat.activeSharesCollectionCount + studentStat.pendingSharesCollectionCount,
        ]
      })

      const filename = this.intl.t('pages.bookLibrary.shares.byStudent.exportCsv.filename', {
        date: this.intl.formatDate(new Date()).replace(/\//g, '-'),
      })

      this.fileDownload.downloadAsCsv([header, ...rows], { filename, header: true })
    } else {
      const shares = await this.querySharesForExport.perform()

      const header = [
        this.intl.t('pages.bookLibrary.shares.byCollection.exportCsv.headers.collectionName'),
        this.intl.t('pages.bookLibrary.shares.byCollection.exportCsv.headers.sharedBy'),
        this.intl.t('pages.bookLibrary.shares.byCollection.exportCsv.headers.startDate'),
        this.intl.t('pages.bookLibrary.shares.byCollection.exportCsv.headers.endDate'),
        this.intl.t('pages.bookLibrary.shares.byCollection.exportCsv.headers.status'),
      ]

      const rows = shares.map((share) => {
        return [
          share.collection.name,
          share.collection.teacher?.fullName ?? '-',
          this.intl.formatDate(share.startsAt),
          this.intl.formatDate(share.expiresAt),
          this.intl.t(`pages.bookLibrary.shares.byCollection.exportCsv.shareStatus.${share.status}`),
        ]
      })

      const fileNameKey = this.status.includes(ShareStatus.Expired)
        ? `pages.bookLibrary.shares.byCollection.exportCsv.pastFilename`
        : `pages.bookLibrary.shares.byCollection.exportCsv.currentFilename`
      const filename = this.intl.t(fileNameKey, { date: this.intl.formatDate(new Date()).replace(/\//g, '-') })

      this.fileDownload.downloadAsCsv([header, ...rows], { filename, header: true })
    }
  }

  <template>
    {{#if this.loadSchoolClasses.isPending}}
      <FidgetLoadingIndicatorComponent
        data-test-classes-loading
        @show={{true}}
        @centered={{true}}
        @appearance="small"
        @overlay={{false}}
      />
    {{else if this.loadSchoolClasses.value}}
      <TabGroupComponent
        class="mx-4 -mb-px"
        @theme={{themeKey @subscriptionType}}
        @tabType={{TAB_TYPE_KEYS.borderUnderline}}
        as |tabGroup|
      >
        <tabGroup.linkToTab
          @route="subscription-type.book-library.shares.index.by-collection"
          @query={{hash
            status=(array this.ShareStatus.Pending this.ShareStatus.Active)
            schoolClassId=this.SchoolClassOptionTypes.My
          }}
          @currentWhen={{this.isCurrentSharesTab}}
        >
          {{t "bookLibrary.pages.sharedCollections.tabs.current"}}
        </tabGroup.linkToTab>
        <tabGroup.linkToTab
          @route="subscription-type.book-library.shares.index.by-collection"
          @query={{hash status=(array this.ShareStatus.Expired) schoolClassId=this.SchoolClassOptionTypes.My}}
          @currentWhen={{this.isPastSharesTab}}
        >
          {{t "bookLibrary.pages.sharedCollections.tabs.past"}}
        </tabGroup.linkToTab>
        <tabGroup.linkToTab
          @route="subscription-type.book-library.shares.index.by-student"
          @query={{hash tab=this.ByStudentTabs.MyStudents schoolClassId=this.SchoolClassOptionTypes.My}}
          @currentWhen={{this.isMyStudentsTab}}
        >
          {{t "bookLibrary.pages.sharedCollections.tabs.myStudents"}}
        </tabGroup.linkToTab>
        {{#if this.isSubco}}
          <tabGroup.linkToTab
            @route="subscription-type.book-library.shares.index.by-student"
            @query={{hash tab=this.ByStudentTabs.AllStudents schoolClassId=this.SchoolClassOptionTypes.All}}
            @currentWhen={{this.isAllStudentsTab}}
          >
            {{t "bookLibrary.pages.sharedCollections.tabs.allStudents"}}
          </tabGroup.linkToTab>
        {{/if}}
      </TabGroupComponent>
      <div class="border-dusty-black-100 space-y-6 overflow-x-auto rounded-md border pt-6 shadow-md">
        <div class="mx-6 flex gap-4">
          <label class="space-x-4 font-normal">
            <span>{{t "schoolClass.filter.label"}}</span>
            <SchoolClassSelect
              data-test-school-class-select
              @additionalOptions={{this.schoolClassAdditionalOptions}}
              @schoolClasses={{if
                this.isAllStudentsTab
                this.loadSchoolClasses.value.allClasses
                this.loadSchoolClasses.value.myClasses
              }}
              @schoolClassId={{this.schoolClassId}}
              @updateSchoolClassId={{this.updateSchoolClassId}}
              @themeKey={{themeKey @subscriptionType}}
            />
            <BookLibraryCollectionStats />
          </label>
          <UiButton
            data-test-export-csv
            class="ml-auto inline-flex items-center gap-1"
            @disabled={{or this.querySharesForExport.isRunning this.queryStudentsForExport.isRunning}}
            {{on "click" this.exportCsv}}
          >
            <PhosphorIcon @icon="phosphor-file-x-fill" />
            {{t "pages.bookLibrary.shares.exportCsvButton"}}
          </UiButton>
          <InputText
            data-test-search-input
            type="search"
            role="searchbox"
            name="search-filter"
            value={{this.search}}
            aria-label={{t "pages.bookLibrary.shares.searchPlaceholder"}}
            placeholder={{t "pages.bookLibrary.shares.searchPlaceholder"}}
            @themeKey={{themeKey @subscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" this.changeSearch)}}
          />
        </div>
        {{yield to="table"}}
      </div>
    {{else if this.loadSchoolClasses.isError}}
      <div data-test-classes-error>
        <p class="mb-3">{{t "resourceRequestError.explanationPlural"}}</p>
        <p class="mb-3">{{t "ifErrorPersistsContactSupport"}}</p>
      </div>
    {{/if}}
  </template>
}

export default PageBookLibraryShares
